'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import NextImage from 'next/image';
import NextLink from 'next/link';
import type {FC} from 'react';
import {useAfterFirstPaint} from '../../hooks';
import {RichContent} from '../article';
import {Button, Typography, VisuallyHidden} from '../primitives';

export interface AboutSectionMedia {
  leftBackground: string;
  rightBackground: string;
  leftCircle?: string;
  rightCircle?: string;
}

export interface AboutSectionProps {
  title: string;
  description: string;
  cta: {label: string; href: string};
  media: AboutSectionMedia;
  onCtaClick?: () => void;
}

const PANEL_HEIGHT_DESKTOP = 700;
const PHOTO_HEIGHT_MOBILE = 360;
const LENS_DESKTOP = 468;
const LENS_MOBILE = 269;
const TEXT_TOP_CLEARANCE_MOBILE = 158;
const TEXT_MAX_WIDTH = 360;

// The desktop lens straddles the panel seam at the section's midpoint, so exactly half
// of it sits over the text. Pinning it to its 1440-frame pixel size makes that overhang
// grow as the seam moves left, and the text disappears under it. Sizing it as a share of
// the section keeps the frame's proportions at any width.
const DESKTOP_FRAME_WIDTH = 1440;
const shareOfFrame = (px: number) => `min(${px}px, ${((px / DESKTOP_FRAME_WIDTH) * 100).toFixed(3)}%)`;
const LENS_DESKTOP_SIZE = shareOfFrame(LENS_DESKTOP);
const LENS_DESKTOP_OVERHANG = shareOfFrame(LENS_DESKTOP / 2);

// This section is full-bleed, so it can't sit inside the shared Container — but its text
// still has to line up with every other section's content column. Mirror the Container's
// geometry from base-tokens (a 1160 content box inside a 32px gutter)
// so the heading shares a start edge with the page. Percentages resolve against the
// section, since the panel's containing block is the section.
const PAGE_CONTENT_WIDTH = 1160;
const PAGE_GUTTER_DESKTOP = 32;
const TEXT_PAD_START_DESKTOP = `max(${PAGE_GUTTER_DESKTOP}px, calc((100% - ${PAGE_CONTENT_WIDTH}px) / 2))`;

// Each panel is half the row on desktop and full-width stacked on mobile; the lens
// halves are a fixed-size disc split in two.
const PANEL_SIZES = '(max-width: 900px) 100vw, 50vw';
const LENS_HALF_SIZES = `(max-width: 900px) ${LENS_MOBILE}px, ${LENS_DESKTOP / 2}px`;

interface CoverImageProps {
  src: string;
  alt: string;
  fill: true;
  sizes: string;
}

const CoverImage = styled(NextImage)({
  position: 'absolute',
  inset: 0,
  zIndex: -1,
  objectFit: 'cover',
  objectPosition: 'center',
}) as FC<CoverImageProps>;

const Root = styled('section')(({theme}) => ({
  position: 'relative',
  isolation: 'isolate',
  overflow: 'hidden',
  width: '100%',
  display: 'flex',
  flexDirection: 'column-reverse',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    // minHeight, not height: the description is CMS-authored, so longer copy must grow
    // the section rather than be silently cut off by the overflow:hidden above.
    minHeight: PANEL_HEIGHT_DESKTOP,
  },
}));

const TextPanel = styled(Box)(({theme}) => ({
  position: 'relative',
  isolation: 'isolate',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  color: theme.palette.colors.text.neutral.inverse,
  paddingBlockStart: TEXT_TOP_CLEARANCE_MOBILE,
  paddingBlockEnd: theme.spacing(4),
  paddingInline: theme.spacing(2.25),
  [theme.breakpoints.up('md')]: {
    flex: '1 1 50%',
    justifyContent: 'center',
    paddingBlock: theme.spacing(8),
    paddingInlineStart: TEXT_PAD_START_DESKTOP,
    // Reserve the lens overhang, plus a gap, so the text can never run under the circle
    // at any width — the column absorbs the difference instead.
    paddingInlineEnd: `calc(${LENS_DESKTOP_OVERHANG} + ${theme.spacing(1.75)})`,
  },
}));

const PhotoPanel = styled(Box)(({theme}) => ({
  position: 'relative',
  isolation: 'isolate',
  width: '100%',
  height: PHOTO_HEIGHT_MOBILE,
  backgroundColor: theme.palette.colors.surface.neutral.subtle,
  [theme.breakpoints.up('md')]: {flex: '1 1 50%', height: 'auto'},
}));

const Lens = styled(Box)(({theme}) => ({
  position: 'absolute',
  left: '50%',
  top: PHOTO_HEIGHT_MOBILE,
  transform: 'translate(-50%, -50%)',
  zIndex: 1,
  width: LENS_MOBILE,
  // Height follows the width — a percentage height would resolve against the section's
  // height, not its width, and flatten the circle into an ellipse.
  aspectRatio: '1',
  borderRadius: theme.radii.full,
  overflow: 'hidden',
  boxShadow: theme.customShadows.card,
  display: 'flex',
  // Mobile stacks the two half-disc assets into a top/bottom split; desktop lays
  // them out left/right. The circle itself is the container clip (overflow + pill
  // radius); each half's mobile seam anchor lives on LensHalf.
  flexDirection: 'column-reverse',
  [theme.breakpoints.up('md')]: {
    top: '50%',
    width: LENS_DESKTOP_SIZE,
    flexDirection: 'row',
  },
}));

// The lens assets are portrait left/right half-discs (flat edge on the vertical
// seam) built for the desktop left/right split. Stacked top/bottom on mobile,
// `cover` crops to a horizontal band whose curved arc edge falls short of the
// seam and exposes the fallback fill as a white sliver. Each disc is full-width
// (fully opaque) only at its vertical centre, so anchor the half's seam-facing
// edge there: the top half shows its upper band, the bottom half its lower band.
const LENS_SEAM_ANCHOR = {top: '50% 33%', bottom: '50% 67%'} as const;

const LensHalf = styled(Box)(({theme}) => ({
  position: 'relative',
  isolation: 'isolate',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  backgroundColor: theme.palette.colors.surface.neutral.subtle,
  transform: theme.direction === 'rtl' ? 'rotateY(-180deg)' : undefined,
}));

const LensHalfImage = styled(NextImage, {shouldForwardProp: propName => propName !== 'slot'})<{slot: keyof typeof LENS_SEAM_ANCHOR}>(
  ({theme, slot}) => ({
    objectFit: 'cover',
    objectPosition: LENS_SEAM_ANCHOR[slot],
    // Desktop meets the halves flat-edge-to-flat-edge (left/right) — centre works.
    [theme.breakpoints.up('md')]: {objectPosition: 'center'},
  }),
) as FC<CoverImageProps & {slot: keyof typeof LENS_SEAM_ANCHOR}>;

const Content = styled(Box)(({theme}) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2.25),
  maxWidth: TEXT_MAX_WIDTH,
}));

export function AboutSection({title, description, cta, media, onCtaClick}: AboutSectionProps) {
  const showImages = useAfterFirstPaint();
  return (
    <Root>
      <TextPanel>
        {showImages && <CoverImage src={media.leftBackground} alt="" fill sizes={PANEL_SIZES} />}
        <Content>
          <Typography
            size="title-2"
            weight="bold"
            mobileSize="title-5"
            mobileWeight="bold"
            as="h2"
            sx={{color: theme => theme.palette.colors.text.neutral.inverse, textTransform: 'capitalize'}}
          >
            {title}
          </Typography>
          <RichContent html={description} collapsible={false} sx={{color: theme => theme.palette.colors.text.neutral.inverse}} />
          <Button variant="outlined-light" component={NextLink} href={cta.href} onClick={onCtaClick}>
            {cta.label}
            <VisuallyHidden> {title}</VisuallyHidden>
          </Button>
        </Content>
      </TextPanel>

      <PhotoPanel>{showImages && <CoverImage src={media.rightBackground} alt="" fill sizes={PANEL_SIZES} />}</PhotoPanel>

      {media.leftCircle && media.rightCircle && (
        <Lens aria-hidden>
          <LensHalf>{showImages && <LensHalfImage src={media.leftCircle} alt="" fill sizes={LENS_HALF_SIZES} slot="bottom" />}</LensHalf>
          <LensHalf>{showImages && <LensHalfImage src={media.rightCircle} alt="" fill sizes={LENS_HALF_SIZES} slot="top" />}</LensHalf>
        </Lens>
      )}
    </Root>
  );
}

AboutSection.displayName = 'AboutSection';
